import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  Renderer2,
  afterRender,
  booleanAttribute,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { PtlTooltipBubbleComponent } from './ptl-tooltip-bubble.component';
import { positionTooltip, trackTooltip } from './ptl-tooltip-placement';

@Component({
  selector: 'ptl-tooltip',
  standalone: true,
  imports: [PtlTooltipBubbleComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ptl-tooltip.component.html',
  styleUrls: ['./ptl-tooltip.component.scss'],
})
export class PtlTooltipComponent {
  /**
   * One piece of text, or its parts. Parts share a line, joined by `separator`, while the window has room for
   * them; otherwise each gets a line of its own. Empty parts are left out.
   */
  text = input<string | readonly string[]>('');
  separator = input(' · ');
  /** The side it prefers. It opens on another when there is no room there. */
  direction = input<'top' | 'right' | 'bottom' | 'left'>('top');
  /**
   * Where a `top` or `bottom` bubble prefers to sit along the trigger: centred on it, or lined up with its start or
   * end edge. It shifts along as far as it must to stay inside the window either way.
   */
  align = input<'center' | 'start' | 'end'>('center');
  /**
   * Also opens on keyboard focus, and a click keeps it open until the trigger is clicked again, Escape, or a
   * click elsewhere. For an explainer behind an info icon: a touch screen cannot hover, and a keyboard never
   * fires `mouseenter`.
   */
  toggleOnClick = input(false, { transform: booleanAttribute });
  /** Wraps at a readable width instead of the window's — for a sentence rather than a label. */
  multiline = input(false, { transform: booleanAttribute });

  protected readonly parts = computed(() => {
    const text = this.text();
    return (typeof text === 'string' ? [text] : text).filter((part) => !!part);
  });

  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly trigger = viewChild.required<ElementRef<HTMLElement>>('trigger');
  private readonly bubble = viewChild('bubble', { read: ElementRef<HTMLElement> });

  protected readonly hovered = signal(false);
  private readonly focused = signal(false);
  private readonly pinned = signal(false);
  protected readonly show = computed(() => this.hovered() || this.focused() || this.pinned());

  /** Held only while pinned: a page can carry dozens of plain tooltips (chart cells) that never need them. */
  private stopListening: (() => void)[] = [];
  /** The bubble on show, and what keeps it next to its trigger; held only while it is shown, like the above. */
  private tracked: { bubble: HTMLElement; stop: () => void } | null = null;

  constructor() {
    // After every render, so it also follows a change of text or a trigger that moved. A closed tooltip stops at
    // the missing bubble.
    afterRender(() => this.place());
    inject(DestroyRef).onDestroy(() => {
      this.unpin();
      this.untrack();
    });
  }

  /**
   * Closing clears hover and focus as well as the pin. A click also focuses the button, and the pointer is
   * still over it — keeping either would leave the bubble up and make the closing click look dead.
   */
  protected onClick(): void {
    if (!this.toggleOnClick()) {
      return;
    }
    if (this.pinned()) {
      this.dismiss();
    } else {
      this.pin();
    }
  }

  protected onFocusIn(): void {
    if (this.toggleOnClick()) {
      this.focused.set(true);
    }
  }

  protected onFocusOut(event: FocusEvent): void {
    if (!this.host.nativeElement.contains(event.relatedTarget as Node | null)) {
      this.focused.set(false);
    }
  }

  protected onEscape(): void {
    if (this.toggleOnClick()) {
      this.dismiss();
    }
  }

  private dismiss(): void {
    this.hovered.set(false);
    this.focused.set(false);
    this.unpin();
  }

  /**
   * The outside click is caught on the way down (capture phase). Controls that stop their click from bubbling,
   * such as a card's other buttons, would otherwise leave this bubble pinned open next to theirs. The click that
   * pins it is already past the document at this point, so it does not immediately close it again.
   */
  private pin(): void {
    this.pinned.set(true);
    const onOutsideClick = (event: MouseEvent) => {
      if (!this.host.nativeElement.contains(event.target as Node | null)) {
        this.dismiss();
      }
    };
    this.document.addEventListener('click', onOutsideClick, true);
    this.stopListening = [
      () => this.document.removeEventListener('click', onOutsideClick, true),
      this.renderer.listen('document', 'keydown.escape', () => this.dismiss()),
    ];
  }

  private unpin(): void {
    this.pinned.set(false);
    this.stopListening.forEach((stop) => stop());
    this.stopListening = [];
  }

  private place(): void {
    const bubble = this.bubble()?.nativeElement;
    if (bubble !== this.tracked?.bubble) {
      this.untrack();
      if (bubble) {
        // Outside Angular: scrolling past an open bubble must not run change detection on every frame.
        const stop = this.zone.runOutsideAngular(() => trackTooltip(bubble, this.trigger().nativeElement, () => this.position(bubble)));
        this.tracked = { bubble, stop };
      }
    }
    if (bubble) {
      this.position(bubble);
    }
  }

  private position(bubble: HTMLElement): void {
    positionTooltip(bubble, this.trigger().nativeElement, { side: this.direction(), align: this.align() });
  }

  private untrack(): void {
    this.tracked?.stop();
    this.tracked = null;
  }
}
